import { Check } from 'lucide-react'
import { getFileIconForArtifact } from '@/components/project/artifact-viewer/fileIcons'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { useTranslation } from '@/hooks/useTranslation'
import { cn } from '@/lib/utils'
import type { ArtifactCardProps } from './types'
import { formatArtifactLabel } from './utils'

/**
 * Reusable card component for displaying artifacts in the analysis modal.
 * Supports both single-select mode (for primary file) and multi-select mode (for context files).
 *
 * @param props - Component props
 * @param props.artifact - The artifact to display
 * @param props.isSelected - Whether this artifact is currently selected
 * @param props.mode - Selection mode: 'single' for primary file selection, 'multi' for context file selection
 * @param props.onSelect - Callback when artifact is selected (single-select mode)
 * @param props.onToggle - Callback when artifact is toggled (multi-select mode)
 */
export function ArtifactCard({
	artifact,
	isSelected,
	mode,
	onSelect,
	onToggle,
	disabled = false,
}: ArtifactCardProps) {
	const { t } = useTranslation()
	const classificationLabel = formatArtifactLabel(artifact, t)
	const isInteractionDisabled = disabled && !isSelected

	const handleClick = () => {
		if (isInteractionDisabled) return

		if (mode === 'single' && onSelect) {
			onSelect(artifact)
		} else if (mode === 'multi' && onToggle) {
			onToggle(artifact)
		}
	}

	return (
		<Card
			onClick={handleClick}
			className={cn(
				'transition-all duration-200 p-2',
				isInteractionDisabled
					? 'cursor-not-allowed opacity-50 ring-1 ring-border/50'
					: 'cursor-pointer',
				isSelected
					? 'ring-1 ring-primary/50 bg-primary/5'
					: 'ring-1 ring-border/50 hover:ring-border hover:bg-muted/30'
			)}
		>
			<CardContent className="p-2.5 flex items-center gap-2.5">
				{mode === 'multi' && (
					<Checkbox
						checked={isSelected}
						disabled={isInteractionDisabled}
						onChange={() => onToggle?.(artifact)}
						onClick={(e) => e.stopPropagation()}
					/>
				)}

				<div
					className={cn(
						'rounded-md bg-muted/60 flex items-center justify-center shrink-0',
						mode === 'single' ? 'h-9 w-9' : 'h-8 w-8'
					)}
				>
					{(() => {
						const Icon = getFileIconForArtifact(artifact, artifact.extension)
						return <Icon className="h-4 w-4 text-muted-foreground" />
					})()}
				</div>

				<div className="flex-1 min-w-0">
					<p className="text-sm font-medium text-foreground truncate">{artifact.name}</p>
					{classificationLabel && (
						<p className="text-xs text-muted-foreground truncate">{classificationLabel}</p>
					)}
				</div>

				<span className="text-xs text-muted-foreground uppercase font-medium tracking-wide shrink-0">
					{artifact.extension}
				</span>

				{mode === 'single' && isSelected && (
					<div className="h-5 w-5 rounded-full bg-primary flex items-center justify-center shrink-0">
						<Check className="h-3 w-3 text-primary-foreground" strokeWidth={3} />
					</div>
				)}
			</CardContent>
		</Card>
	)
}
